/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { OperationalConfirmBody } from '../../shared/modals/OperationalConfirmModal'
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import type { CustomerVoidPlan } from '../../customers/public-api';
import { formatMoney } from '../../shared/format/public-api';

const NARROW_MAX_WIDTH = 420;
const MIN_REASON_LENGTH = 4;

type VoidSaleConfirmModalProps = {
  visible: boolean;
  saleTotal: number;
  /** Referencia visible de la venta (`#1042`) — se usa en el estado de éxito. */
  saleRefLabel?: string | null;
  loading: boolean;
  /** Post-anulación: el modal queda abierto mostrando el resumen de lo que pasó. */
  success?: boolean;
  isFiscalSale?: boolean;
  hasMercadoPagoQrPayment?: boolean;
  hasCommissions?: boolean;
  customerVoidPlan?: CustomerVoidPlan | null;
  customerPreviewLoading?: boolean;
  /** When true, require supervisor PIN to authorize void without canVoid. */
  requireSupervisorPin?: boolean;
  onClose: () => void;
  onConfirm: (reason: string, supervisorPin?: string) => void | Promise<void>;
};

/**
 * Cajas de impacto con jerarquía (proto ventas-ux-v1): warn-box para fiscal
 * y MP QR, info-box para comisiones y cuenta de cliente. Antes eran textos
 * sueltos dentro del confirm body.
 */
function VoidImpactHints({
  isFiscalSale,
  hasMercadoPagoQrPayment,
  hasCommissions,
  customerVoidPlan,
  customerPreviewLoading,
  showCustomerImpact,
}: {
  isFiscalSale: boolean;
  hasMercadoPagoQrPayment: boolean;
  hasCommissions: boolean;
  customerVoidPlan: CustomerVoidPlan | null;
  customerPreviewLoading: boolean;
  showCustomerImpact: boolean;
}) {
  return (
    <>
      {isFiscalSale ? (
        <View style={[styles.hintBox, styles.hintBoxWarn]}>
          <Text style={styles.hintBoxWarnText}>
            Si la factura ya salió, ARCA emite una nota de crédito.
          </Text>
        </View>
      ) : null}
      {hasMercadoPagoQrPayment ? (
        <View style={[styles.hintBox, styles.hintBoxWarn]}>
          <Text style={styles.hintBoxWarnText}>
            Hay un cobro con QR Mercado Pago. Devolvé la plata desde Mercado Pago.
          </Text>
        </View>
      ) : null}
      {hasCommissions ? (
        <View style={[styles.hintBox, styles.hintBoxInfo]}>
          <Text style={styles.hintBoxInfoText}>
            Las comisiones generadas por esta venta también se anulan.
          </Text>
        </View>
      ) : null}
      {customerPreviewLoading ? (
        <View style={styles.customerPreviewRow}>
          <ActivityIndicator size="small" color={ticketColors.ink2} />
          <Text style={styles.extraHintInline}>Viendo qué pasa con la cuenta…</Text>
        </View>
      ) : null}
      {showCustomerImpact ? (
        <View style={[styles.hintBox, styles.hintBoxInfo]}>
          <Text style={styles.customerImpactTitle}>Qué pasa con la cuenta</Text>
          {customerVoidPlan!.totalReleased > 0 ? (
            <Text style={styles.hintBoxInfoText}>
              Se sueltan {formatMoney(customerVoidPlan!.totalReleased)} cobrados a esta
              venta.
            </Text>
          ) : null}
          {customerVoidPlan!.refifoTargets.length > 0 ? (
            <>
              <Text style={styles.hintBoxInfoText}>
                Esa plata se imputa a otras deudas del cliente:
              </Text>
              {customerVoidPlan!.refifoTargets.map((t) => (
                <Text key={`${t.saleId}-${t.amount}`} style={styles.customerRefifoLine}>
                  · Venta #{t.shortId}: {formatMoney(t.amount)}
                </Text>
              ))}
            </>
          ) : customerVoidPlan!.totalReleased > 0 ? (
            <Text style={styles.hintBoxInfoText}>
              No tiene otras deudas. Le queda a favor.
            </Text>
          ) : null}
          {customerVoidPlan!.compensatingPayments.length > 0 ? (
            <Text style={styles.hintBoxInfoText}>
              Se saca el cargo a cuenta de esta venta.
            </Text>
          ) : null}
        </View>
      ) : null}
    </>
  );
}

/** Éxito post-anulación (proto): resumen de lo que pasó + filas kv. */
function VoidSuccessBody({
  reason,
  saleRefLabel,
  isFiscalSale,
  customerVoidPlan,
}: {
  reason: string;
  saleRefLabel?: string | null;
  isFiscalSale: boolean;
  customerVoidPlan: CustomerVoidPlan | null;
}) {
  const released = customerVoidPlan?.totalReleased ?? 0;
  return (
    <View style={{ gap: 10 }}>
      <View style={[styles.hintBox, styles.hintBoxSuccess]}>
        <Text style={styles.hintBoxSuccessText}>
          <Text style={{ fontWeight: '800' }}>Listo.</Text> El stock volvió y los cobros
          quedaron anulados.
          {isFiscalSale ? ' La nota de crédito está en camino a ARCA.' : ''}
        </Text>
      </View>
      {reason.trim().length > 0 ? (
        <View style={styles.successKv}>
          <Text style={styles.successKvLabel}>Motivo</Text>
          <Text style={styles.successKvValue}>{reason.trim()}</Text>
        </View>
      ) : null}
      {isFiscalSale ? (
        <View style={styles.successKv}>
          <Text style={styles.successKvLabel}>Nota de crédito</Text>
          <Text style={[styles.successKvValue, { color: ticketColors.warn }]}>Emitiendo…</Text>
        </View>
      ) : null}
      {released > 0 ? (
        <View style={styles.successKv}>
          <Text style={styles.successKvLabel}>Cuenta del cliente</Text>
          <Text style={[styles.successKvValue, { color: ticketColors.ok }]}>
            {formatMoney(released)} a favor
          </Text>
        </View>
      ) : null}
    </View>
  );
}

export function VoidSaleConfirmModal({
  visible,
  saleTotal,
  saleRefLabel = null,
  loading,
  success = false,
  isFiscalSale = false,
  hasMercadoPagoQrPayment = false,
  hasCommissions = false,
  customerVoidPlan = null,
  customerPreviewLoading = false,
  requireSupervisorPin = false,
  onClose,
  onConfirm,
}: VoidSaleConfirmModalProps) {
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web';
  const narrow = width < NARROW_MAX_WIDTH;
  const useSheet = isMobile || narrow;
  const [reason, setReason] = useState('');
  const [supervisorPin, setSupervisorPin] = useState('');

  useEffect(() => {
    if (!visible) {
      setReason('');
      setSupervisorPin('');
    }
  }, [visible]);

  const trimmed = reason.trim();
  const pinOk = !requireSupervisorPin || supervisorPin.trim().length >= 4;
  const canSubmit = trimmed.length >= MIN_REASON_LENGTH && pinOk && !loading && !customerPreviewLoading;

  const handleClose = () => {
    if (loading || customerPreviewLoading) return;
    onClose();
  };

  const showCustomerImpact = customerVoidPlan?.hasCustomerImpact === true;

  if (useSheet) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
        <View style={styles.sheetBackdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
          <View style={[styles.sheet, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
            <View style={styles.grabber} />
            <TicketModalHeader
              kicker="Ventas"
              title={success ? 'Venta anulada' : 'Anular venta'}
              subtitle={
                success && saleRefLabel
                  ? `${saleRefLabel} · Total ${formatMoney(saleTotal)}`
                  : `Total ${formatMoney(saleTotal)}`
              }
              onClose={handleClose}
              compact
            />

            <ModalAwareScrollView
              style={styles.sheetScroll}
              contentContainerStyle={styles.sheetScrollContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              bottomOffset={spacing.sm}
            >
              {success ? (
                <VoidSuccessBody
                  reason={trimmed}
                  saleRefLabel={saleRefLabel}
                  isFiscalSale={isFiscalSale}
                  customerVoidPlan={customerVoidPlan}
                />
              ) : (
                <>
                  <OperationalConfirmBody
                    destructive
                    iconName="ban-outline"
                    message="La anulás y vuelve el stock."
                    cleanDesign
                  >
                    <VoidImpactHints
                      isFiscalSale={isFiscalSale}
                      hasMercadoPagoQrPayment={hasMercadoPagoQrPayment}
                      hasCommissions={hasCommissions}
                      customerVoidPlan={customerVoidPlan}
                      customerPreviewLoading={customerPreviewLoading}
                      showCustomerImpact={showCustomerImpact}
                    />
                  </OperationalConfirmBody>

                  <View style={styles.reasonFieldHeader}>
                    <Text style={styles.fieldLabel}>Motivo de anulación</Text>
                    <Text
                      style={[
                        styles.reasonCounter,
                        trimmed.length >= MIN_REASON_LENGTH ? styles.reasonCounterOk : null,
                      ]}
                    >
                      {trimmed.length}/{MIN_REASON_LENGTH} mín.
                    </Text>
                  </View>
                  <TextInput
                    style={styles.reasonInput}
                    value={reason}
                    onChangeText={setReason}
                    placeholder="Ej.: error de carga, cliente desistió…"
                    placeholderTextColor={ticketColors.ink2}
                    multiline
                    editable={!loading}
                    accessibilityLabel="Motivo de anulación"
                  />
                  {requireSupervisorPin ? (
                    <>
                      <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>PIN de supervisor</Text>
                      <TextInput
                        style={styles.reasonInput}
                        value={supervisorPin}
                        onChangeText={(t) => setSupervisorPin(t.replace(/[^\d]/g, '').slice(0, 12))}
                        placeholder="PIN"
                        placeholderTextColor={ticketColors.ink2}
                        keyboardType="number-pad"
                        secureTextEntry
                        maxLength={12}
                        editable={!loading}
                        accessibilityLabel="PIN de supervisor"
                      />
                    </>
                  ) : null}
                </>
              )}
            </ModalAwareScrollView>

            <View style={styles.sheetFooter}>
              {success ? (
                <TouchableOpacity
                  style={styles.sheetBtnSuccess}
                  onPress={handleClose}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={styles.sheetBtnSuccessText}>Cerrar</Text>
                </TouchableOpacity>
              ) : (
                <>
                  <TouchableOpacity
                    style={styles.sheetBtnCancel}
                    onPress={handleClose}
                    disabled={loading}
                    accessibilityRole="button"
                  >
                    <Text style={styles.sheetBtnCancelText}>Cancelar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.sheetBtnDanger, !canSubmit && styles.sheetBtnDisabled]}
                    onPress={() => void onConfirm(trimmed, requireSupervisorPin ? supervisorPin.trim() : undefined)}
                    disabled={!canSubmit}
                    accessibilityRole="button"
                    accessibilityLabel="Confirmar anulación"
                  >
                    {loading ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.sheetBtnDangerText}>Anular venta</Text>
                    )}
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, modalBackdropPadding]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleClose}
          accessible={false}
        />
        <View style={[posUi.modalCard, modalExperience.modalWidthStandard, styles.cardMax, styles.card]}>
          <TicketModalHeader
            kicker="Ventas"
            title={success ? 'Venta anulada' : 'Anular venta'}
            subtitle={
              success && saleRefLabel
                ? `${saleRefLabel} · Total ${formatMoney(saleTotal)}`
                : `Total ${formatMoney(saleTotal)}`
            }
            onClose={handleClose}
          />
          {success ? (
            <>
              <View style={{ paddingHorizontal: spacing.md, paddingTop: spacing.sm }}>
                <VoidSuccessBody
                  reason={trimmed}
                  saleRefLabel={saleRefLabel}
                  isFiscalSale={isFiscalSale}
                  customerVoidPlan={customerVoidPlan}
                />
              </View>
              <View style={[posUi.modalActions, modalExperience.footerActions]}>
                <TouchableOpacity
                  style={[posUi.primaryButton, { flex: 1 }]}
                  onPress={handleClose}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={posUi.primaryButtonText}>Cerrar</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              <OperationalConfirmBody
                destructive
                iconName="ban-outline"
                message="La anulás y vuelve el stock."
              >
                <VoidImpactHints
                  isFiscalSale={isFiscalSale}
                  hasMercadoPagoQrPayment={hasMercadoPagoQrPayment}
                  hasCommissions={hasCommissions}
                  customerVoidPlan={customerVoidPlan}
                  customerPreviewLoading={customerPreviewLoading}
                  showCustomerImpact={showCustomerImpact}
                />
              </OperationalConfirmBody>
              <View style={styles.reasonFieldHeader}>
                <Text style={styles.fieldLabel}>Motivo de anulación</Text>
                <Text
                  style={[
                    styles.reasonCounter,
                    trimmed.length >= MIN_REASON_LENGTH ? styles.reasonCounterOk : null,
                  ]}
                >
                  {trimmed.length}/{MIN_REASON_LENGTH} mín.
                </Text>
              </View>
              <TextInput
                style={styles.reasonInput}
                value={reason}
                onChangeText={setReason}
                placeholder="Ej.: error de carga, cliente desistió…"
                placeholderTextColor={ticketColors.ink2}
                multiline
                editable={!loading}
                accessibilityLabel="Motivo de anulación"
              />
              {requireSupervisorPin ? (
                <>
                  <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>PIN de supervisor</Text>
                  <TextInput
                    style={styles.reasonInput}
                    value={supervisorPin}
                    onChangeText={(t) => setSupervisorPin(t.replace(/[^\d]/g, '').slice(0, 12))}
                    placeholder="PIN"
                    placeholderTextColor={ticketColors.ink2}
                    keyboardType="number-pad"
                    secureTextEntry
                    maxLength={12}
                    editable={!loading}
                    accessibilityLabel="PIN de supervisor"
                  />
                </>
              ) : null}
              <View style={[posUi.modalActions, modalExperience.footerActions]}>
                <TouchableOpacity
                  style={[posUi.secondaryButton, posUi.modalButtonHalf]}
                  onPress={handleClose}
                  disabled={loading}
                  accessibilityRole="button"
                >
                  <Text style={posUi.secondaryButtonText}>Cancelar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.dangerButton,
                    posUi.modalButtonHalf,
                    !canSubmit && posUi.disabledCard,
                  ]}
                  onPress={() => void onConfirm(trimmed, requireSupervisorPin ? supervisorPin.trim() : undefined)}
                  disabled={!canSubmit}
                  accessibilityRole="button"
                  accessibilityLabel="Confirmar anulación"
                >
                  {loading ? (
                    <ActivityIndicator color={ticketColors.bad} />
                  ) : (
                    <Text style={styles.dangerButtonText}>Anular venta</Text>
                  )}
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
  },
  cardMax: {
    maxWidth: 440,
  },
  // ── Cajas de impacto (proto ventas-ux-v1: bad/warn/info-box) ──
  hintBox: {
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 6,
    marginTop: 8,
    width: '100%',
  },
  hintBoxWarn: {
    backgroundColor: ticketColors.warnBg,
    borderColor: ticketColors.warnBorder,
  },
  hintBoxWarnText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.warn,
    lineHeight: 18,
  },
  hintBoxInfo: {
    backgroundColor: ticketColors.paper2,
    borderColor: ticketColors.ruleSoft,
  },
  hintBoxInfoText: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink3,
    lineHeight: 18,
  },
  hintBoxSuccess: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorder,
  },
  hintBoxSuccessText: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.brandInk,
    lineHeight: 18,
  },
  // ── Éxito post-anulación: filas kv ──
  successKv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  successKvLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  successKvValue: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    flexShrink: 1,
    textAlign: 'right',
  },
  sheetBtnSuccess: {
    flex: 1,
    height: 46,
    borderRadius: 12,
    backgroundColor: ticketColors.brandPrimary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetBtnSuccessText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  extraHintInline: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink2,
    marginLeft: spacing.sm,
  },
  customerPreviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  customerImpactTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  customerRefifoLine: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    lineHeight: 17,
    marginLeft: spacing.xs,
  },
  reasonFieldHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink2,
    flex: 1,
    minWidth: 0,
  },
  reasonCounter: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink3,
    flexShrink: 0,
  },
  reasonCounterOk: {
    color: ticketColors.ok,
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    color: ticketColors.ink,
    padding: 12,
    paddingHorizontal: 14,
    minHeight: 60,
    textAlignVertical: 'top',
    fontSize: 15,
    fontWeight: '500',
  },
  dangerButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  dangerButtonText: {
    ...typography.body,
    color: '#B45309',
    fontWeight: '700',
  },

  // ── Bottom sheet (mobile/narrow) ──
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheet: {
    width: '100%',
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    maxHeight: '96%',
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.ruleSoft,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    alignItems: 'center',
  },
  sheetFooter: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    paddingHorizontal: 20,
    paddingTop: spacing.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  sheetBtnCancel: {
    flexGrow: 1,
    flexBasis: 140,
    minHeight: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  sheetBtnCancelText: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
    textAlign: 'center',
  },
  sheetBtnDanger: {
    flexGrow: 1.5,
    flexBasis: 160,
    minHeight: 44,
    borderRadius: 10,
    backgroundColor: '#B45309',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  sheetBtnDangerText: {
    fontSize: 15,
    fontWeight: '800',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  sheetBtnDisabled: {
    opacity: 0.5,
  },
});
